import { MAIN_PANEL_COMPONENTS } from '@src/components/AppLayout/Panels/Trading/main';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { useAppLayoutConfig } from '@src/providers/AppLayoutConfig/AppLayoutConfigContext';
import type { LayoutSpec, PanelComponentSpec } from '@src/types/LayoutConfig';
import {
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  notEmpty,
  SubTabBar,
  useMixpanel,
  useSecuritiesContext,
  type SubTabBarAddOption,
  type TabBarItem,
} from '@talos/kyoko';
import type { GroupviewPanelState } from 'dockview';
import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTradingPanelRoute } from '../Blotters/navRoutes';
import { useMobileTradingNavItems } from '../useMobileTradingNavItems';
import { isEnabledMarketTabPanelType, MARKET_TAB_PANEL_TYPES, type MarketTabPanelType } from './marketTabPanelTypes';
import { getLayoutWithAddedMarketPanel, type MarketPanelComponentSpec } from './utils/getLayoutWithAddedMarketPanel';
import { getLayoutWithoutMarketPanel } from './utils/getLayoutWithoutMarketPanel';

interface MobileMarketPanelProps {
  layout: LayoutSpec;
  panel: GroupviewPanelState;
  children: ReactNode;
}

/**
 * Shared mobile market shell: keeps the market-local sub-navigation mounted while switching between
 * Market Cards and Watchlist, so SubTabBar state/animations are not reset by remounts.
 */
export function MobileMarketPanel({ layout, panel, children }: MobileMarketPanelProps) {
  const navigate = useNavigate();
  const { updateLayoutConfig } = useAppLayoutConfig('trading');
  const { enablePredictionMarkets } = useFeatureFlag();
  const { searchableSecurities } = useSecuritiesContext();
  const mixpanel = useMixpanel(MixpanelEventSource.MarketsPanel);
  const { shouldConfirmCloseTab } = useDisplaySettings();
  const tradingNavItems = useMobileTradingNavItems();
  const parent = tradingNavItems.find(item => item.id === MARKET_NAV_ID);
  const marketPanelIds = useMemo(
    () => parent?.children?.map(child => child.id).filter((panelId): panelId is string => panelId != null) ?? [],
    [parent?.children]
  );
  const dockViewLayoutRef = useRef(layout.dockViewLayout);
  const handleNavigate = useCallback((path: string) => navigate(path), [navigate]);
  const addableMarketPanelSpecs = useMemo(
    () =>
      MARKET_ADD_PANEL_SPECS.filter(panelSpec => isEnabledMarketTabPanelType(panelSpec.type, enablePredictionMarkets)),
    [enablePredictionMarkets]
  );

  const handleAddMarketTab = useCallback(
    (panelType: MarketTabPanelType) => {
      const nextMarketPanel = getLayoutWithAddedMarketPanel({
        panelType,
        panelSpecs: addableMarketPanelSpecs,
        sourceDockViewLayout: dockViewLayoutRef.current,
        targetPanelId: panel.id,
        searchableSecurities,
      });
      if (nextMarketPanel == null) {
        return;
      }
      const { dockViewLayout, newPanelId } = nextMarketPanel;

      dockViewLayoutRef.current = dockViewLayout;
      updateLayoutConfig({ id: layout.id, dockViewLayout: dockViewLayout });
      mixpanel.track(MixpanelEvent.AddTab, {
        [MixpanelEventProperty.TabType]: panelType,
      });
      navigate(getTradingPanelRoute(layout, newPanelId));
    },
    [addableMarketPanelSpecs, layout, mixpanel, navigate, panel.id, searchableSecurities, updateLayoutConfig]
  );

  const handleRenameMarketTab = useCallback(
    (item: TabBarItem, label: string) => {
      if (item.id == null) {
        return;
      }

      const dockViewLayout = structuredClone(dockViewLayoutRef.current);
      const renamedPanel = dockViewLayout.panels[item.id];
      if (renamedPanel == null) {
        return;
      }

      renamedPanel.title = label;
      renamedPanel.params = {
        ...renamedPanel.params,
        customTitle: true,
      };
      dockViewLayoutRef.current = dockViewLayout;
      updateLayoutConfig({ id: layout.id, dockViewLayout });
      mixpanel.track(MixpanelEvent.RenameTab, {
        [MixpanelEventProperty.TabType]: renamedPanel.contentComponent,
      });
    },
    [layout.id, mixpanel, updateLayoutConfig]
  );
  const handleDeleteMarketTab = useCallback(
    (item: TabBarItem) => {
      if (item.id == null) {
        return;
      }

      const deletedPanel = dockViewLayoutRef.current.panels[item.id];
      const nextMarketPanel = getLayoutWithoutMarketPanel({
        currentPanelId: panel.id,
        deletedPanelId: item.id,
        marketPanelIds,
        sourceDockViewLayout: dockViewLayoutRef.current,
      });
      if (nextMarketPanel == null) {
        return;
      }

      dockViewLayoutRef.current = nextMarketPanel.dockViewLayout;
      updateLayoutConfig({ id: layout.id, dockViewLayout: nextMarketPanel.dockViewLayout });
      mixpanel.track(MixpanelEvent.CloseTab, {
        [MixpanelEventProperty.TabType]: deletedPanel?.contentComponent,
      });
      navigate(getTradingPanelRoute(layout, nextMarketPanel.nextPanelId));
    },
    [layout, marketPanelIds, mixpanel, navigate, panel.id, updateLayoutConfig]
  );
  useEffect(() => {
    dockViewLayoutRef.current = layout.dockViewLayout;
  }, [layout.dockViewLayout]);

  const addOptions = useMemo<SubTabBarAddOption[]>(
    () =>
      addableMarketPanelSpecs.map(panelSpec => ({
        id: panelSpec.type,
        label: panelSpec.label,
        icon: panelSpec.icon,
        onSelect: () => handleAddMarketTab(panelSpec.type),
      })),
    [addableMarketPanelSpecs, handleAddMarketTab]
  );

  return (
    <>
      {children}
      {parent?.children?.length != null && parent.children.length > 0 && (
        <SubTabBar
          parent={parent}
          onNavigate={handleNavigate}
          addOptions={addOptions}
          onRenameItem={handleRenameMarketTab}
          onDeleteItem={handleDeleteMarketTab}
          shouldConfirmDeleteItem={shouldConfirmCloseTab}
        />
      )}
    </>
  );
}

const MARKET_NAV_ID = 'market';
const MARKET_ADD_PANEL_SPECS = MARKET_TAB_PANEL_TYPES.map(panelType =>
  MAIN_PANEL_COMPONENTS.find(isPanelSpecForType(panelType))
).filter(notEmpty);

function isPanelSpecForType(panelType: MarketTabPanelType) {
  return (panel: PanelComponentSpec): panel is MarketPanelComponentSpec => panel.type === panelType;
}
